<template>
  <div class="space-y-6">
    <!-- Mapping Info Section -->
    <div class="bg-white p-6 rounded-lg border">
      <h3 class="text-lg font-medium text-gray-900 mb-4">
        {{ $t('representative.recordMapping.mappingInfo') }}
      </h3>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <!-- First Column: Representative, Created Date, Mapping Type, Skip First Line -->
        <div class="space-y-4">
          <!-- Representative ID -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              {{ $t('representative.recordMapping.representative') }}
            </label>
            <div class="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-gray-500">
              {{ representativeCode || parsedSchema.MappingInfo.RepresentativeID || '-' }}
            </div>
          </div>

          <!-- Created Date -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              {{ $t('representative.recordMapping.createdDate') }}
            </label>
            <DateTimePicker
              ref="createdDatePickerRef"
              v-model="createdDateForPicker"
              :placeholder="$t('representative.recordMapping.createdDate')"
              :show-time-picker="true"
              show-error-as-tooltip
              :disabled="mode === 'view'"
              @update:modelValue="onDateChange" />
          </div>

          <!-- Mapping Type -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              {{ $t('representative.recordMapping.mappingType') }}
            </label>
            <BaseSelectDropdown
              :items="dropdownMappingTypes"
              :selected-item="selectedMappingType"
              @update:selected="handleMappingTypeSelection"
              name="mappingType"
              :mode="mode"
              clearable />
          </div>

          <!-- Skip First Line -->
          <div class="flex flex-col">
            <label class="block text-sm font-medium text-gray-700 mb-1">
              {{ $t('representative.recordMapping.skipFirstLine') }}
            </label>
            <input
              v-model="parsedSchema.MappingInfo.SkipFirstLine"
              type="number"
              min="0"
              :disabled="mode === 'view'"
              class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-theme-500 focus:border-theme-500 disabled:bg-gray-50 disabled:text-gray-500" />
          </div>
        </div>

        <!-- Second Column - Description -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            {{ $t('representative.recordMapping.description') }}
          </label>
          <textarea
            v-model="parsedSchema.MappingInfo.Description"
            rows="12"
            :disabled="mode === 'view'"
            class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-theme-500 focus:border-theme-500 disabled:bg-gray-50 disabled:text-gray-500 resize-none"></textarea>
        </div>
      </div>
    </div>

    <!-- Concatenation Settings -->
    <div class="bg-white p-6 rounded-lg border">
      <h3 class="text-lg font-medium text-gray-900 mb-4">
        {{ $t('representative.recordMapping.concatenationSettings') }}
      </h3>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <!-- First Column: Concatenation Settings -->
        <div class="space-y-4">
          <!-- Default Separator -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              {{ $t('representative.recordMapping.defaultSeparator') }}
            </label>
            <input
              v-model="parsedSchema.MappingInfo.ConcatenationSettings.DefaultSeparator"
              type="text"
              :disabled="mode === 'view'"
              class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-theme-500 focus:border-theme-500 disabled:bg-gray-50 disabled:text-gray-500" />
          </div>

          <!-- Allow Custom Separators -->
          <div class="flex flex-col">
            <label class="block text-sm font-medium text-gray-700 mb-1">
              {{ $t('representative.recordMapping.allowCustomSeparators') }}
            </label>
            <BaseToggle
              class="flex-1"
              v-model="parsedSchema.MappingInfo.ConcatenationSettings.AllowCustomSeparators"
              name="allowCustomSeparators"
              :disable="mode === 'view'" />
          </div>

          <!-- Include Separator In Source -->
          <div class="flex flex-col">
            <label class="block text-sm font-medium text-gray-700 mb-1">
              {{ $t('representative.recordMapping.includeSeparatorInSource') }}
            </label>
            <BaseToggle
              class="flex-1"
              v-model="parsedSchema.MappingInfo.ConcatenationSettings.IncludeSeparatorInSource"
              name="includeSeparatorInSource"
              :disable="mode === 'view'" />
          </div>
        </div>

        <!-- Second Column - Concatenation Description -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            {{ $t('representative.recordMapping.concatenationDescription') }}
          </label>
          <textarea
            v-model="parsedSchema.MappingInfo.ConcatenationSettings.Description"
            rows="8"
            :disabled="mode === 'view'"
            class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-theme-500 focus:border-theme-500 disabled:bg-gray-50 disabled:text-gray-500 resize-none"></textarea>
        </div>
      </div>
    </div>

    <!-- Import Settings (Dynamic based on MappingType) -->
    <div class="bg-white p-6 rounded-lg border">
      <h3 class="text-lg font-medium text-gray-900 mb-4">
        {{ $t('representative.recordMapping.importSettings') }}
      </h3>

      <!-- CSV Settings -->
      <div
        v-if="parsedSchema.MappingInfo.MappingType === MappingType.CSV"
        class="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            {{ $t('representative.recordMapping.separator') }}
          </label>
          <input
            v-model="parsedSchema.ImportSettings.CSV.Separator"
            type="text"
            :disabled="mode === 'view'"
            class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-theme-500 focus:border-theme-500 disabled:bg-gray-50 disabled:text-gray-500" />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            {{ $t('representative.recordMapping.quoteCharacter') }}
          </label>
          <input
            v-model="parsedSchema.ImportSettings.CSV.QuoteCharacter"
            type="text"
            maxlength="1"
            :disabled="mode === 'view'"
            class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-theme-500 focus:border-theme-500 disabled:bg-gray-50 disabled:text-gray-500" />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            {{ $t('representative.recordMapping.encoding') }}
          </label>
          <input
            v-model="parsedSchema.ImportSettings.CSV.Encoding"
            type="text"
            :disabled="mode === 'view'"
            class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-theme-500 focus:border-theme-500 disabled:bg-gray-50 disabled:text-gray-500" />
        </div>
      </div>

      <!-- Fixed Length Settings -->
      <div
        v-else-if="parsedSchema.MappingInfo.MappingType === MappingType.FixedLength"
        class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            {{ $t('representative.recordMapping.recordLength') }}
          </label>
          <input
            v-model.number="parsedSchema.ImportSettings.FixedLength.RecordLength"
            type="number"
            min="1"
            :disabled="mode === 'view'"
            class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-theme-500 focus:border-theme-500 disabled:bg-gray-50 disabled:text-gray-500" />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            {{ $t('representative.recordMapping.padCharacter') }}
          </label>
          <input
            v-model="parsedSchema.ImportSettings.FixedLength.PadCharacter"
            type="text"
            :disabled="mode === 'view'"
            class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-theme-500 focus:border-theme-500 disabled:bg-gray-50 disabled:text-gray-500" />
        </div>
      </div>

      <!-- JSON Settings -->
      <div
        v-else-if="parsedSchema.MappingInfo.MappingType === MappingType.JSON"
        class="grid grid-cols-1 gap-4">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              {{ $t('representative.recordMapping.rootPath') }}
            </label>
            <input
              v-model="parsedSchema.ImportSettings.JSON.RootPath"
              type="text"
              :disabled="mode === 'view'"
              class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-theme-500 focus:border-theme-500 disabled:bg-gray-50 disabled:text-gray-500" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              {{ $t('representative.recordMapping.arrayPath') }}
            </label>
            <input
              v-model="parsedSchema.ImportSettings.JSON.ArrayPath"
              type="text"
              :disabled="mode === 'view'"
              class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-theme-500 focus:border-theme-500 disabled:bg-gray-50 disabled:text-gray-500" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              {{ $t('representative.recordMapping.encoding') }}
            </label>
            <input
              v-model="parsedSchema.ImportSettings.JSON.Encoding"
              type="text"
              :disabled="mode === 'view'"
              class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-theme-500 focus:border-theme-500 disabled:bg-gray-50 disabled:text-gray-500" />
          </div>
          <div class="flex flex-col">
            <label class="block text-sm font-medium text-gray-700 mb-1">
              {{ $t('representative.recordMapping.validateSchema') }}
            </label>
            <BaseToggle
              class="flex-1"
              v-model="parsedSchema.ImportSettings.JSON.ValidateSchema"
              name="validateSchema"
              :disable="mode === 'view'" />
          </div>
        </div>
      </div>

      <!-- Database Settings -->
      <div
        v-else-if="parsedSchema.MappingInfo.MappingType === MappingType.Database"
        class="grid grid-cols-1 gap-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            {{ $t('representative.recordMapping.connectionString') }}
          </label>
          <textarea
            v-model="parsedSchema.ImportSettings.Database.ConnectionString"
            rows="2"
            :disabled="mode === 'view'"
            class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-theme-500 focus:border-theme-500 disabled:bg-gray-50 disabled:text-gray-500"></textarea>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            {{ $t('representative.recordMapping.query') }}
          </label>
          <textarea
            v-model="parsedSchema.ImportSettings.Database.Query"
            rows="4"
            :disabled="mode === 'view'"
            class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-theme-500 focus:border-theme-500 disabled:bg-gray-50 disabled:text-gray-500"></textarea>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            {{ $t('representative.recordMapping.commandTimeout') }}
          </label>
          <input
            v-model.number="parsedSchema.ImportSettings.Database.CommandTimeout"
            type="number"
            min="0"
            :disabled="mode === 'view'"
            class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-theme-500 focus:border-theme-500 disabled:bg-gray-50 disabled:text-gray-500" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import BaseSelectDropdown from '@/components/common/BaseSelectDropdown.vue';
import BaseToggle from '@/components/common/BaseToggle.vue';
import DateTimePicker from '@/components/common/DateTimePicker.vue';
import { MappingType } from '@/types/RecordMapping';
import { enumToDropdownItems } from '@/utils/enums';
import { isoDateToDatepickerFormat, datepickerToServerDateFormat } from '@/utils/date';
import type { SelectDropdownItem } from '@/types/Base';
import type { RecordMappingSchema } from '@/types/RecordMapping';

const { t: $t } = useI18n();

// Props
defineProps<{
  mode: 'view' | 'edit';
  representativeCode: string;
}>();

// v-model for the parsed schema
const parsedSchema = defineModel<RecordMappingSchema>('parsedSchema', { required: true });

// Date picker ref for validation access
const createdDatePickerRef = ref();

const emit = defineEmits(['dateValidationChange']);

// Computed properties
const dropdownMappingTypes = computed(() => enumToDropdownItems(MappingType));
const selectedMappingType = computed(() => {
  return (
    dropdownMappingTypes.value.find(
      (x: SelectDropdownItem) => x.value === parsedSchema.value.MappingInfo.MappingType
    ) || null
  );
});

const createdDateForPicker = computed({
  get: () => {
    return isoDateToDatepickerFormat(parsedSchema.value.MappingInfo.CreatedDate);
  },
  set: (value: string) => {
    if (!value) {
      parsedSchema.value.MappingInfo.CreatedDate = '';
      return;
    }
    parsedSchema.value.MappingInfo.CreatedDate = datepickerToServerDateFormat(value);
  },
});

// Event handlers
function handleMappingTypeSelection(selectedItem: SelectDropdownItem | null) {
  if (selectedItem) {
    parsedSchema.value.MappingInfo.MappingType = selectedItem.value as number;
  }
}

function onDateChange() {
  const validation = createdDatePickerRef.value?.getValidationState() || {
    isValid: true,
    error: '',
  };
  emit('dateValidationChange', validation);
}

function getDateValidation() {
  return (
    createdDatePickerRef.value?.getValidationState() || {
      isValid: true,
      error: '',
    }
  );
}

defineExpose({
  getDateValidation,
});
</script>
